import * as PIXI from "pixi.js";
import anime from "animejs";
import TickerTimeout from "../../../utils/TickerTimeout";
import { PixiSettings } from "../../../pixiSettings";
import { ASPECT_RATIO_BREAKPOINTS, ULTRA_WIDE } from "../../../Contexts/DimensionsContext";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import {
  setShowPotCollectionIntro,
  setPotCollectionTransitionActive,
} from "../../../State/actions";

// ─── Overlay ──────────────────────────────────────────────────────────────────
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.85;
const OVERLAY_FADE_DURATION = 350;

// ─── Skip protection ─────────────────────────────────────────────────────────
const SKIP_PROTECTION_MS = 1000;

// ============================================================================
// LAYOUT CONFIGURATION — identical to IntroPopupComposedController
// ============================================================================

const LAYOUT_MOBILE_SMALL_PORTRAIT = {
  bgScaleMult: 1.0,
  padMult: 0.5,
  sidePadMult: 0.1,
  descFontRatio: 0.075,
  descBoxHRatio: 0.23,
};

const LAYOUT_MOBILE_SMALL_LANDSCAPE = {
  bgScaleMult: 1.00,
  padMult: 0.75,
  sidePadMult: 0.5,
  descFontRatio: 0.075,
  descBoxHRatio: 0.23,
};

const LAYOUT_MOBILE_PORTRAIT = {
  bgScaleMult: 1.00,
  padMult: 0.5,
  sidePadMult: 0.1,
  descFontRatio: 0.065,
  descBoxHRatio: 0.22,
};

const LAYOUT_MOBILE_LANDSCAPE = {
  bgScaleMult: 1.0,
  padMult: 0.75,
  sidePadMult: 0.5,
  descFontRatio: 0.075,
  descBoxHRatio: 0.21,
};

const LAYOUT_TABLET_PORTRAIT = {
  bgScaleMult: 1.05,
  padMult: 1.0,
  sidePadMult: 0.2,
  descFontRatio: 0.075,
  descBoxHRatio: 0.175,
};

const LAYOUT_TABLET_LANDSCAPE = {
  bgScaleMult: 0.9,
  padMult: 1.0,
  sidePadMult: 0.5,
  descFontRatio: 0.075,
  descBoxHRatio: 0.17,
};

const LAYOUT_SMALL_DESKTOP = {
  bgScaleMult: 1.3,
  padMult: 1.0,
  sidePadMult: 0.5,
  descFontRatio: 0.070,
  descBoxHRatio: 0.15,
};

const LAYOUT_DESKTOP = {
  bgScaleMult: 1.2,
  padMult: 1.0,
  sidePadMult: 1.0,
  descFontRatio: 0.070,
  descBoxHRatio: 0.15,
};

const LAYOUT_WIDE_DESKTOP = {
  bgScaleMult: 1.1,
  padMult: 1.0,
  sidePadMult: 1.0,
  descFontRatio: 0.070,
  descBoxHRatio: 0.15,
};

const LAYOUT_ULTRA_WIDE_DESKTOP = {
  bgScaleMult: 1.05,
  padMult: 1.0,
  sidePadMult: 1.0,
  descFontRatio: 0.070,
  descBoxHRatio: 0.15,
};


const LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: LAYOUT_MOBILE_SMALL_PORTRAIT,
  MOBILE_SMALL_LANDSCAPE: LAYOUT_MOBILE_SMALL_LANDSCAPE,
  MOBILE_PORTRAIT: LAYOUT_MOBILE_PORTRAIT,
  MOBILE_LANDSCAPE: LAYOUT_MOBILE_LANDSCAPE,
  TABLET_PORTRAIT: LAYOUT_TABLET_PORTRAIT,
  TABLET_LANDSCAPE: LAYOUT_TABLET_LANDSCAPE,
  SMALL_DESKTOP: LAYOUT_SMALL_DESKTOP,
  DESKTOP: LAYOUT_DESKTOP,
  WIDE_DESKTOP: LAYOUT_WIDE_DESKTOP,
  ULTRA_WIDE_DESKTOP: LAYOUT_ULTRA_WIDE_DESKTOP,
};

const getLayoutConfig = (device, isPortrait, isMobileSmall, screenWidth) => {
  const orientation = isPortrait ? "PORTRAIT" : "LANDSCAPE";
  if (isMobileSmall) return LAYOUT_CONFIGS[`MOBILE_SMALL_${orientation}`];
  if (device === PixiSettings.DEVICE.MOBILE) return LAYOUT_CONFIGS[`MOBILE_${orientation}`];
  if (device === PixiSettings.DEVICE.TABLET) return LAYOUT_CONFIGS[`TABLET_${orientation}`];
  if (screenWidth && screenWidth >= ULTRA_WIDE.BREAKPOINT) return LAYOUT_CONFIGS.ULTRA_WIDE_DESKTOP;
  if (screenWidth && screenWidth >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) return LAYOUT_CONFIGS.WIDE_DESKTOP;
  if (screenWidth && screenWidth <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH) return LAYOUT_CONFIGS.SMALL_DESKTOP;
  return LAYOUT_CONFIGS.DESKTOP;
};

// ---------------------------------------------------------------------------
// PORTRAIT element ratios — identical to IntroPopupComposedController
// ---------------------------------------------------------------------------
const PORTRAIT_CFG = {
  // Shared (locked to bg, independent of card count)
  youWonFontRatio: 0.07,
  youWonYRatio: -0.395,
  logoWRatio: 0.8,
  logoYRatio: -0.31,
  tapFontRatio: 0.035,
  tapYRatio: 0.37,

  // 1 card — single centered
  cards1: {
    cardWRatio: 0.55,
    cardYRatio: -0.23,
  },

  // 2 cards — diagonal (first upper-left, second lower-right)
  cards2: {
    cardWRatio: 0.45,
    card1XRatio: -0.23,
    card1YRatio: -0.23,
    card2XRatio: 0.22,
    card2YRatio: -0.05,
  },

  // 3 cards — first on top, two below
  cards3: {
    topWRatio: 0.37,
    topYRatio: -0.26,
    sideWRatio: 0.37,
    sideYRatio: 0.03,
    sideGapRatio: 0.07,
  },
};

// ---------------------------------------------------------------------------
// LANDSCAPE element ratios
// ---------------------------------------------------------------------------
const LANDSCAPE_CFG = {
  // Shared
  youWonFontRatio: 0.034,
  youWonYRatio: -0.47,
  logoWRatio: 0.35,
  logoYRatio: -0.36,
  tapFontRatio: 0.0225,
  tapYRatio: 0.56,

  // All card counts use same layout in landscape (row)
  cards3: {
    cardWRatio: 0.24,
    cardYRatio: 0.02,
    cardGapRatio: 0.05,
  },
};

// ─── Per-card scale multipliers ──────────────────────────────────────────────
const CARD_SCALE = {
  M: 1.10,
  C: 1.04,
  D: 1.00,
};

// Card composited from two split images (pot + coins) stacked directly, with
// a per-feature gap fine-tune — same technique as PaytableOverlay's feature
// slides, reusing the same already-shipped split assets instead of a third
// redundant combined image per feature.
const COINS_GAP_MUL = {
  C: 0.00,
  M: -0.01,
  D: -0.05,
};

// Feature definitions in display order: C → M → D (client-requested)
// typeCode maps to characters in respinGameType (e.g. "DMC", "MC", "D")
const FEATURE_DEFS = [
  { typeCode: "C", potTexName: "intro_feature_collect_pot", coinsTexName: "intro_feature_collect_coins", key: "start_page_collect_title", defaultValue: "Collects values" },
  { typeCode: "M", potTexName: "intro_feature_mystery_pot", coinsTexName: "intro_feature_mystery_coins", key: "start_page_mystery_title", defaultValue: "Turns into Jackpots" },
  { typeCode: "D", potTexName: "intro_feature_double_pot", coinsTexName: "intro_feature_double_coins", key: "start_page_double_title", defaultValue: "2x all values" },
];


/**
 * PotCollectionIntroController — Image-based pot collection intro popup.
 * Same layout as IntroPopupComposedController but with:
 * - zeus_respins_logo instead of game logo
 * - "YOU WON" text instead of "MAX WIN 10,000X"
 * - Fade-in/fade-out overlay animation
 * - Skip protection (1s before tap enabled)
 * - "TAP TO CONTINUE" / "CLICK TO CONTINUE" text
 * - Dispatches setPotCollectionTransitionActive on close
 */
class PotCollectionIntroController {
  constructor(container, dimensionsContext, dispatch, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.t = t;

    // Display objects
    this._overlay = null;     // dark overlay (fade in/out)
    this._blackBg = null;
    this._stormBg = null;
    this._logo = null;        // zeus_respins_logo
    this._youWonText = null;  // "YOU WON" (replaces "MAX WIN")
    this._featureCards = [];
    this._cardsGroup = null;       // wrapper for card containers
    this._tapContainer = null;
    this._tapText = null;
    this._contentContainer = null; // groups all content (for fade)

    // Textures
    this._bgPortraitTex = PIXI.Assets.get("intro_background_portrait");
    this._bgLandscapeTex = PIXI.Assets.get("intro_background_landscape");
    this._logoTex = PIXI.Assets.get("zeus_respins_logo");
    // Reference split textures (collect pair) for shared layout ratios —
    // landscape row height + shared desc box height. Any feature's pair
    // works here since collect/mystery/double all share nearly identical
    // pot/coins aspect ratios.
    this._refPotTex = PIXI.Assets.get("intro_feature_collect_pot");
    this._refCoinsTex = PIXI.Assets.get("intro_feature_collect_coins");

    // Animation state
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    // Hover / press animation refs
    this._hoverAnim = null;
    this._pressAnim = null;
    this._isPressAnimating = false;

    // Animation refs for cleanup
    this._overlayAnim = null;
    this._contentAnim = null;
    this._tapFadeAnim = null;
    this._cardAnims = null;

    // Timeouts
    this._skipProtectionTimeout = null;
    this._debounceTimeout = null;

    // Active features (set by startSequence, default to all)
    this._activeFeatures = FEATURE_DEFS;
    this._gameType = "DMC";

    // Click handler (set externally via setClickHandler)
    this._clickHandler = null;

    this.initialized = false;
    this._initialize();

    // Hide container entirely until startSequence — prevents any single-frame flash
    this.container.visible = false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  _initialize() {
    this.container.sortableChildren = true;

    // Overlay — sits behind content, full screen
    this._createOverlay();

    // Content container — groups everything for fade in/out
    this._contentContainer = new PIXI.Container();
    this._contentContainer.name = "pc_intro_content";
    this._contentContainer.sortableChildren = true;
    this._contentContainer.zIndex = 1;
    this._contentContainer.alpha = 0; // hidden until startSequence
    this._contentContainer.eventMode = "none"; // no hit-testing until active
    this.container.addChild(this._contentContainer);

    this._createBlackBackground();
    this._createStormBackground();
    this._createLogo();
    this._createYouWonText();
    this._createFeatureCards();
    this._createTapText();

    this.initialized = true;
    this._updateLayout();
  }

  _createOverlay() {
    this._overlay = new PIXI.Graphics();
    this._overlay.name = "pc_intro_overlay";
    this._overlay.zIndex = 0;
    this._overlay.alpha = 0;
    this._overlay.eventMode = "none";
    this.container.addChild(this._overlay);
  }

  _drawOverlay() {
    const { width, height } = this.dimensionsContext.dimensions;
    this._overlay.clear();
    this._overlay.beginFill(OVERLAY_BACKGROUND_COLOR);
    this._overlay.drawRect(0, 0, width, height);
    this._overlay.endFill();
  }

  _createBlackBackground() {
    this._blackBg = new PIXI.Sprite(PIXI.Texture.WHITE);
    this._blackBg.tint = 0x000000;
    this._blackBg.zIndex = 0;
    this._blackBg.eventMode = "none";
    // Default (arrow) cursor — the background itself isn't a button, only the
    // "tap to continue" text shows a hand cursor.
    this._blackBg.cursor = "default";
    // Background click → same as tap-to-start (fullscreen + dismiss)
    this._blackBg.on("pointertap", (e) => {
      if (this._clickHandler) this._clickHandler(e);
    });
    this._contentContainer.addChild(this._blackBg);
  }

  _createStormBackground() {
    const isPortrait = this.dimensionsContext.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const tex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    if (!tex) return;

    this._stormBg = new PIXI.Sprite(tex);
    this._stormBg.anchor.set(0.5);
    this._stormBg.zIndex = 0.5;
    this._stormBg.eventMode = "none";
    this._contentContainer.addChild(this._stormBg);
  }

  _createLogo() {
    if (!this._logoTex) return;

    this._logo = new PIXI.Sprite(this._logoTex);
    this._logo.anchor.set(0.5);
    this._logo.zIndex = 1;
    this._logo.eventMode = "none";
    this._contentContainer.addChild(this._logo);
  }

  _createYouWonText() {
    // `popup_zeus_respins_...`, with the zeus_ — that is the key the locale
    // files actually carry, in all fifteen of them. The lookup here was missing
    // that one word, so i18next found nothing and returned the key, which this
    // line then shouted in upper case across the popup.
    const text = this.t("popup_zeus_respins_feature_entry_title").toUpperCase();
    const style = new PIXI.TextStyle(scaleStyle(FONT_STYLES.winText, 24, { align: "center" }));

    this._youWonText = new PIXI.Text(text, style);
    this._youWonText.anchor.set(0.5);
    this._youWonText.resolution = 2;
    this._youWonText.zIndex = 1;
    this._youWonText.eventMode = "none";
    this._contentContainer.addChild(this._youWonText);
  }

  _createFeatureCards() {
    // Group container for all cards — enables unified scale/fade from bg center
    this._cardsGroup = new PIXI.Container();
    this._cardsGroup.name = "cards_group";
    this._cardsGroup.zIndex = 1;
    this._contentContainer.addChild(this._cardsGroup);

    for (const def of FEATURE_DEFS) {
      const potTex = PIXI.Assets.get(def.potTexName);
      const coinsTex = PIXI.Assets.get(def.coinsTexName);
      const desc = this.t(def.key, { defaultValue: def.defaultValue });

      const cardContainer = new PIXI.Container();
      cardContainer.name = `feature_card_${def.typeCode}`;
      cardContainer.zIndex = 1;
      cardContainer.eventMode = "none";

      // Pot (top) + coins (bottom), stacked directly — same composited
      // technique as PaytableOverlay's feature slides. anchor (0.5, 0) so
      // Y positioning is a simple top-down stack: potSprite.y = 0,
      // coinsSprite.y = potSprite.height + gap.
      const potSprite = new PIXI.Sprite(potTex || PIXI.Texture.EMPTY);
      potSprite.anchor.set(0.5, 0);
      potSprite.eventMode = "none";

      const coinsSprite = new PIXI.Sprite(coinsTex || PIXI.Texture.EMPTY);
      coinsSprite.anchor.set(0.5, 0);
      coinsSprite.eventMode = "none";

      const descText = new PIXI.Text(desc, new PIXI.TextStyle({ fontSize: 12, fill: 0xffffff }));
      descText.anchor.set(0.5);
      descText.resolution = 2;
      descText.eventMode = "none";

      cardContainer.addChild(potSprite);
      cardContainer.addChild(coinsSprite);
      cardContainer.addChild(descText);
      this._cardsGroup.addChild(cardContainer);

      this._featureCards.push({
        container: cardContainer,
        potSprite,
        coinsSprite,
        descText,
        typeCode: def.typeCode,
        desc,
        potTex,
        coinsTex,
      });
    }
  }

  _createTapText() {
    this._tapContainer = new PIXI.Container();
    this._tapContainer.zIndex = 2;

    const text = this.t("tap_to_start").toUpperCase();
    const style = new PIXI.TextStyle(scaleStyle(FONT_STYLES.introTapToStart, 24, { align: "center" }));

    this._tapText = new PIXI.Text(text, style);
    this._tapText.anchor.set(0.5);
    this._tapText.resolution = 2;
    this._tapText.eventMode = "none"; // activated only inside startSequence()
    this._tapText.cursor = "pointer";

    // Hover effect (replicates useButtonHover: container scale 1.0 → 1.08)
    this._tapText.on("pointerover", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.08,
        y: 1.08,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    this._tapText.on("pointerout", () => {
      if (this._hoverAnim) this._hoverAnim.pause();
      this._hoverAnim = anime({
        targets: this._tapContainer.scale,
        x: 1.0,
        y: 1.0,
        duration: 100,
        easing: "easeOutQuad",
      });
    });

    // Press + click (replicates useButtonHover: scale squish 0.92 → bounce back)
    this._tapText.on("pointertap", (e) => {
      // Stop propagation so background handler doesn't also fire
      if (e?.stopPropagation) e.stopPropagation();
      if (!this._isPressAnimating) {
        this._isPressAnimating = true;
        const origX = this._tapText.scale.x;
        const origY = this._tapText.scale.y;
        if (this._pressAnim) this._pressAnim.pause();
        this._pressAnim = anime({
          targets: this._tapText.scale,
          x: origX * 0.92,
          y: origY * 0.92,
          duration: 65,
          easing: "easeInQuad",
          complete: () => {
            this._pressAnim = anime({
              targets: this._tapText.scale,
              x: origX,
              y: origY,
              duration: 120,
              easing: "easeOutBack",
              complete: () => {
                this._isPressAnimating = false;
              },
            });
          },
        });
      }
      // Trigger close via click handler ref (fullscreen + dismiss)
      if (this._clickHandler) this._clickHandler(e);
    });

    this._tapContainer.addChild(this._tapText);
    this._contentContainer.addChild(this._tapContainer);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════

  setClickHandler(handler) {
    this._clickHandler = handler;
  }

  /**
   * Start the pot collection intro sequence.
   * @param {string} gameType - e.g. "DMC", "MC", "D", "M", "C", "DM", "DC"
   */
  startSequence(gameType = "DMC") {
    if (this.isPlaying) return;

    // Determine active features from gameType
    this._gameType = gameType;
    this._activeFeatures = FEATURE_DEFS.filter(def => gameType.includes(def.typeCode));

    // Show/hide feature cards based on active features
    for (const card of this._featureCards) {
      const isActive = this._activeFeatures.some(f => f.typeCode === card.typeCode);
      card.container.visible = isActive;
    }

    this.isPlaying = true;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    // Make container visible for the sequence
    this.container.visible = true;

    // Clear old timeouts
    this._clearTimeouts();

    // Update layout before showing
    this._updateLayout();

    // Ensure overlay is drawn at current dimensions
    this._drawOverlay();

    // Fade out background music while popup is active
    soundManager.fadeOut(SOUND_NAME.background_music, 500);

    // Play intro sound
    // soundManager.play(SOUND_NAME.free_spin_intro); // TODO: wrong sound, will be replaced

    // Make background + tap text interactive (sides = fullscreen, tap = dismiss)
    this._blackBg.eventMode = "static";
    this._contentContainer.eventMode = "passive";
    this._tapText.eventMode = "static";

    // Fade in overlay
    if (this._overlayAnim) this._overlayAnim.pause();
    this._overlayAnim = anime({
      targets: this._overlay,
      alpha: OVERLAY_BACKGROUND_ALPHA,
      duration: OVERLAY_FADE_DURATION,
      easing: "easeOutQuad",
    });

    // Fade in content (slight delay after overlay) — cards excluded
    if (this._contentAnim) this._contentAnim.pause();
    this._contentContainer.alpha = 0;

    // Hide cards group before content fades in (animates separately)
    if (this._cardsGroup) {
      this._cardsGroup.alpha = 0;
      this._cardsGroup.scale.set(0, 0);
    }

    // Restore tap container visibility (cleared in _finishAnimation)
    if (this._tapContainer) {
      this._tapContainer.alpha = 1;
      this._tapContainer.scale.set(1, 1);
    }

    this._contentAnim = anime({
      targets: this._contentContainer,
      alpha: 1,
      duration: 300,
      delay: 100,
      easing: "easeOutQuad",
      complete: () => {
        // Animate cards group in from center — scale + fade as one unit
        if (!this._cardsGroup) return;
        if (this._cardAnims) this._cardAnims.forEach(a => a.pause());
        this._cardAnims = [];

        this._cardAnims.push(anime({
          targets: this._cardsGroup,
          alpha: 1,
          duration: 500,
          delay: 25,
          easing: "easeOutCubic",
        }));

        this._cardAnims.push(anime({
          targets: this._cardsGroup.scale,
          x: 1,
          y: 1,
          duration: 600,
          delay: 25,
          easing: "easeOutCubic",
        }));
      },
    });

    // Skip protection: enable click after delay (tap text is already visible)
    this._skipProtectionTimeout = new TickerTimeout(() => {
      if (this.isPlaying) {
        this.canSkipAnimation = true;
        this.isWaitingForClick = true;
      }
    }, SKIP_PROTECTION_MS);
  }

  /**
   * Handle user click/tap. Returns true if accepted (triggers close).
   */
  handleUserClick() {
    if (!this.isPlaying) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    // Debounce
    if (this._debounceTimeout) return false;
    this._debounceTimeout = new TickerTimeout(() => {
      this._debounceTimeout = null;
    }, 300);

    this._playClosingAnimation();
    return true;
  }

  updateDimensions(dimensionsContext) {
    this.dimensionsContext = dimensionsContext;

    if (this.initialized) {
      this._updateLayout();
      // Redraw overlay at new dimensions
      if (this._overlay) this._drawOverlay();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLOSING ANIMATION
  // ═══════════════════════════════════════════════════════════════════════════

  _playClosingAnimation() {
    if (!this.isWaitingForClick) return;

    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    // Stop intro sound
    // soundManager.stop(SOUND_NAME.free_spin_intro); // TODO: wrong sound

    // Fade background music back in
    soundManager.fadeIn(SOUND_NAME.background_music, 500);

    // Disable all interaction immediately — nothing should be tappable during fade-out
    if (this._blackBg) this._blackBg.eventMode = "none";
    this._overlay.eventMode = "none";
    this._tapText.eventMode = "none";
    this._contentContainer.eventMode = "none";

    // Dispatch transition BEFORE fade-out starts (so game can prepare)
    if (this.dispatch) {
      this.dispatch(setPotCollectionTransitionActive(true));
    }

    // Fade out content
    if (this._contentAnim) this._contentAnim.pause();
    this._contentAnim = anime({
      targets: this._contentContainer,
      alpha: 0,
      duration: 200,
      easing: "easeInQuad",
    });

    // Fade out overlay (slight delay after content)
    if (this._overlayAnim) this._overlayAnim.pause();
    this._overlayAnim = anime({
      targets: this._overlay,
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: 100,
      easing: "easeInQuad",
      complete: () => {
        this._finishAnimation();
      },
    });
  }

  _finishAnimation() {
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    // Stop sounds
    soundManager.stop(SOUND_NAME.bonus_intro_popup);

    // Clear timeouts
    this._clearTimeouts();

    // Cancel running animations
    this._cancelAnimations();

    // Reset visual state — ALL interactive objects must be silenced to prevent
    // invisible hit areas blocking the play button between rounds
    if (this._contentContainer) {
      this._contentContainer.alpha = 0;
      this._contentContainer.eventMode = "none";
    }
    if (this._blackBg) this._blackBg.eventMode = "none";
    if (this._overlay) {
      this._overlay.alpha = 0;
      this._overlay.eventMode = "none";
    }
    if (this._tapText) this._tapText.eventMode = "none";
    if (this._tapContainer) this._tapContainer.alpha = 0;
    if (this._cardsGroup) {
      this._cardsGroup.alpha = 1;
      this._cardsGroup.scale.set(1, 1);
    }

    // Close dialog
    if (this.dispatch) {
      this.dispatch(setShowPotCollectionIntro(false));
    }

    // Hide container entirely until next startSequence
    if (this.container) this.container.visible = false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT — identical calculations to IntroPopupComposedController
  // ═══════════════════════════════════════════════════════════════════════════

  _updateLayout() {
    const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } = this.dimensionsContext;
    if (!dimensions) return;

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    const { width, height } = dimensions;

    // 1. Padding zones
    const layoutCfg = getLayoutConfig(device, isPortrait, isMobileSmall, width);
    const pad = uiSymbolSize * layoutCfg.padMult;
    const sidePad = uiSymbolSize * layoutCfg.sidePadMult;
    const topEdge = pad;
    const botEdge = height - pad;
    const contentH = botEdge - topEdge;
    const contentW = width - 2 * sidePad;
    const contentCY = topEdge + contentH / 2;

    // 2. Black background
    if (this._blackBg) {
      this._blackBg.x = 0;
      this._blackBg.y = 0;
      this._blackBg.width = width;
      this._blackBg.height = height;
    }

    // 3. Storm cloud background
    const bgTex = isPortrait ? this._bgPortraitTex : this._bgLandscapeTex;
    let bgW, bgH, bgCX, bgCY;

    if (this._stormBg && bgTex) {
      this._stormBg.texture = bgTex;
      const bgScaleMult = layoutCfg.bgScaleMult;
      const bgNativeW = bgTex.width || (isPortrait ? 388 : 812);
      const bgNativeH = bgTex.height || (isPortrait ? 689 : 406);
      const bgScale = Math.min(contentW / bgNativeW, contentH / bgNativeH) * bgScaleMult;
      bgW = bgNativeW * bgScale;
      bgH = bgNativeH * bgScale;
      bgCX = width / 2;
      bgCY = contentCY;

      this._stormBg.x = bgCX;
      this._stormBg.y = bgCY;
      this._stormBg.width = bgW;
      this._stormBg.height = bgH;
    } else {
      bgW = width;
      bgH = height;
      bgCX = width / 2;
      bgCY = contentCY;
    }

    // 4. Orientation config
    const cfg = isPortrait ? PORTRAIT_CFG : LANDSCAPE_CFG;

    // 5. "YOU WON" text (top — above logo)
    if (this._youWonText) {
      const budgetW = bgW * 0.85;
      const youWonStr = this._youWonText.text;
      const base = FONT_STYLES.introTapToStart;
      let fontSize = Math.round(bgW * cfg.youWonFontRatio);
      const tempStyle = new PIXI.TextStyle(scaleStyle(base, fontSize));
      const metrics = PIXI.TextMetrics.measureText(youWonStr, tempStyle);
      if (metrics.width > budgetW) {
        fontSize = Math.round(fontSize * (budgetW / metrics.width));
      }
      this._youWonText.style = new PIXI.TextStyle(scaleStyle(base, fontSize, { align: "center" }));
      this._youWonText.x = width / 2;
      this._youWonText.y = bgCY + bgH * cfg.youWonYRatio;
    }

    // 6. Logo (zeus_respins_logo — below "YOU WON")
    if (this._logo && this._logoTex) {
      const logoW = bgW * cfg.logoWRatio;
      const logoH = logoW / (this._logoTex.width / this._logoTex.height);
      this._logo.x = bgCX;
      this._logo.y = bgCY + bgH * cfg.logoYRatio;
      this._logo.width = logoW;
      this._logo.height = logoH;
    }

    // 7. Feature cards
    this._updateFeatureCards(isPortrait, bgW, bgH, bgCX, bgCY, cfg, layoutCfg);

    // 8. Tap text
    this._updateTapText(bgW, bgH, bgCX, bgCY, cfg, width);
  }

  _updateFeatureCards(isPortrait, bgW, bgH, bgCX, bgCY, cfg, layoutCfg) {
    const activeFeatures = this._activeFeatures || FEATURE_DEFS;
    const activeCards = activeFeatures.map(def =>
      this._featureCards.find(c => c.typeCode === def.typeCode)
    ).filter(Boolean);

    const count = activeCards.length;
    if (count === 0) return;

    // Reference total-composite aspect ratio (pot + gap + coins, at unit
    // width) — used for landscape row height + shared desc box height.
    // Collect's pair used as the representative reference (all 3 pairs
    // share near-identical pot/coins aspect ratios).
    const refPotAR = (this._refPotTex?.width ?? 551) / (this._refPotTex?.height ?? 398);
    const refCoinsAR = (this._refCoinsTex?.width ?? 543) / (this._refCoinsTex?.height ?? 392);
    const refTotalUnitH = 1 / refPotAR + (COINS_GAP_MUL.C ?? 0) + 1 / refCoinsAR;
    const refAR = 1 / refTotalUnitH;

    let cardPositions, cardWidths;

    // Pick the per-count sub-config
    const countKey = `cards${count}`;
    const cc = cfg[countKey] || cfg.cards3;

    if (isPortrait) {
      if (count === 1) {
        const cardW = bgW * cc.cardWRatio;
        const cardX = bgCX - cardW / 2;
        const cardTopY = bgCY + bgH * cc.cardYRatio;
        cardPositions = [{ x: cardX, y: cardTopY }];
        cardWidths = [cardW];
      } else if (count === 2) {
        // Diagonal: card1 upper-left, card2 lower-right
        const cardW = bgW * cc.cardWRatio;
        const card1X = bgCX + bgW * cc.card1XRatio - cardW / 2;
        const card1Y = bgCY + bgH * cc.card1YRatio;
        const card2X = bgCX + bgW * cc.card2XRatio - cardW / 2;
        const card2Y = bgCY + bgH * cc.card2YRatio;
        cardPositions = [
          { x: card1X, y: card1Y },
          { x: card2X, y: card2Y },
        ];
        cardWidths = [cardW, cardW];
      } else {
        // 3 cards — first on top, two below
        const topW = bgW * cc.topWRatio;
        const topX = bgCX - topW / 2;
        const topTopY = bgCY + bgH * cc.topYRatio;

        const sideW = bgW * cc.sideWRatio;
        const gap = bgW * cc.sideGapRatio;
        const sideTopY = bgCY + bgH * cc.sideYRatio;
        const leftX = bgCX - gap / 2 - sideW;
        const rightX = bgCX + gap / 2;

        cardPositions = [
          { x: topX, y: topTopY },
          { x: leftX, y: sideTopY },
          { x: rightX, y: sideTopY },
        ];
        cardWidths = [topW, sideW, sideW];
      }
    } else {
      // Landscape — all cards in a row
      const cardW = bgW * cc.cardWRatio;
      const cardH = cardW / refAR;
      const gap = bgW * (cc.cardGapRatio ?? 0.04);
      const totalW = cardW * count + gap * (count - 1);
      const startX = bgCX - totalW / 2;
      const cardTopY = bgCY + bgH * cc.cardYRatio - cardH / 2;

      cardPositions = [];
      cardWidths = [];
      for (let i = 0; i < count; i++) {
        cardPositions.push({ x: startX + i * (cardW + gap), y: cardTopY });
        cardWidths.push(cardW);
      }
    }

    // Shared desc box height (based on smallest card width for consistency)
    const smallestW = Math.min(...cardWidths);
    const refH = Math.round(smallestW * refTotalUnitH);
    const hRatio = layoutCfg.descBoxHRatio ?? 0.14;
    const sharedDescBoxH = refH * hRatio;

    for (let i = 0; i < count; i++) {
      const card = activeCards[i];
      if (!card) continue;

      const pos = cardPositions[i];
      const baseCardWidth = cardWidths[i];
      const cardWidth = baseCardWidth * (CARD_SCALE[card.typeCode] ?? 1.0);

      card.container.x = pos.x;
      card.container.y = pos.y;

      // Pot (top) + coins (bottom), stacked directly with a per-feature gap.
      const potTex = card.potTex;
      const coinsTex = card.coinsTex;
      const potAR = (potTex?.width ?? cardWidth) / (potTex?.height ?? cardWidth);
      const coinsAR = (coinsTex?.width ?? cardWidth) / (coinsTex?.height ?? cardWidth);
      const potHeight = Math.round(cardWidth / potAR);
      const coinsHeight = Math.round(cardWidth / coinsAR);
      const gapPx = cardWidth * (COINS_GAP_MUL[card.typeCode] ?? 0);
      const coinsY = potHeight + gapPx;

      card.potSprite.x = cardWidth / 2;
      card.potSprite.y = 0;
      card.potSprite.width = cardWidth;
      card.potSprite.height = potHeight;

      card.coinsSprite.x = cardWidth / 2;
      card.coinsSprite.y = coinsY;
      card.coinsSprite.width = cardWidth;
      card.coinsSprite.height = coinsHeight;

      const refWidth = baseCardWidth;
      const maxDescW = refWidth * 0.85;
      const descBoxW = maxDescW;

      const base = FONT_STYLES.introTapToStart;
      let fontSize = Math.round(refWidth * layoutCfg.descFontRatio);
      const measure = (fs) => {
        const s = new PIXI.TextStyle(scaleStyle(base, fs, { wordWrap: true, wordWrapWidth: descBoxW }));
        return PIXI.TextMetrics.measureText(card.desc, s);
      };
      const metrics = measure(fontSize);
      if (metrics.height > sharedDescBoxH) {
        fontSize = Math.round(fontSize * (sharedDescBoxH / metrics.height));
      }
      card.descText.style = new PIXI.TextStyle(scaleStyle(base, fontSize, {
        align: "center",
        wordWrap: true,
        wordWrapWidth: descBoxW,
      }));
      card.descText.x = cardWidth / 2;
      // Centered in the pot↔coins gap
      card.descText.y = potHeight + gapPx / 2;
    }

    // Set group pivot to bg center so all cards scale from one central point
    if (this._cardsGroup) {
      this._cardsGroup.pivot.set(bgCX, bgCY);
      this._cardsGroup.x = bgCX;
      this._cardsGroup.y = bgCY;
    }
  }

  _updateTapText(bgW, bgH, bgCX, bgCY, cfg, screenWidth) {
    if (!this._tapContainer || !this._tapText) return;

    const tapTextY = bgCY + bgH * cfg.tapYRatio;
    const tapFontSize = Math.round(bgW * cfg.tapFontRatio);

    const base = FONT_STYLES.introTapToStart;
    const tapTextStr = this._tapText.text;
    const tempStyle = new PIXI.TextStyle(scaleStyle(base, tapFontSize));
    const metrics = PIXI.TextMetrics.measureText(tapTextStr, tempStyle);
    const finalFontSize = metrics.width > screenWidth * 0.90
      ? Math.round(tapFontSize * (screenWidth * 0.90 / metrics.width))
      : tapFontSize;

    this._tapText.style = new PIXI.TextStyle(scaleStyle(base, finalFontSize, { align: "center" }));
    this._tapContainer.x = bgCX;
    this._tapContainer.y = tapTextY;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  _clearTimeouts() {
    if (this._skipProtectionTimeout) {
      this._skipProtectionTimeout.clear();
      this._skipProtectionTimeout = null;
    }
    if (this._debounceTimeout) {
      this._debounceTimeout.clear();
      this._debounceTimeout = null;
    }
  }

  _cancelAnimations() {
    if (this._overlayAnim) { this._overlayAnim.pause(); this._overlayAnim = null; }
    if (this._contentAnim) { this._contentAnim.pause(); this._contentAnim = null; }
    if (this._tapFadeAnim) { this._tapFadeAnim.pause(); this._tapFadeAnim = null; }
    if (this._hoverAnim) { this._hoverAnim.pause(); this._hoverAnim = null; }
    if (this._pressAnim) { this._pressAnim.pause(); this._pressAnim = null; }
    if (this._cardAnims) { this._cardAnims.forEach(a => a.pause()); this._cardAnims = null; }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  cleanup() {
    // soundManager.stop(SOUND_NAME.free_spin_intro); // TODO: wrong sound
    this._clearTimeouts();
    this._cancelAnimations();

    if (this._overlay) {
      this._overlay.destroy();
      this._overlay = null;
    }
    if (this._blackBg) {
      this._blackBg.removeAllListeners();
      this._blackBg.destroy();
      this._blackBg = null;
    }
    if (this._stormBg) {
      this._stormBg.destroy();
      this._stormBg = null;
    }
    if (this._logo) {
      this._logo.destroy();
      this._logo = null;
    }
    if (this._youWonText) {
      this._youWonText.destroy();
      this._youWonText = null;
    }
    for (const card of this._featureCards) {
      card.container.destroy({ children: true });
    }
    this._featureCards = [];

    if (this._cardsGroup) {
      this._cardsGroup.destroy();
      this._cardsGroup = null;
    }

    if (this._tapContainer) {
      this._tapContainer.destroy({ children: true });
      this._tapContainer = null;
      this._tapText = null;
    }

    if (this._contentContainer) {
      this._contentContainer.destroy({ children: true });
      this._contentContainer = null;
    }

    this.dispatch = null;
    this._clickHandler = null;
    this.initialized = false;
    this.isPlaying = false;
  }
}

export default PotCollectionIntroController;
